import type { TOC } from '@ember/component/template-only'
import { StudentImportPrompt } from 'teacher-ui-client/components/rollover/student-import-prompt'
import { RolloverPreImportSummary } from 'teacher-ui-client/components/rollover/pre-import-summary'
import type ConfirmController from 'teacher-ui-client/manage/rollover/steps/students/import/confirm/controller'
import type ConfirmRoute from 'teacher-ui-client/manage/rollover/steps/students/import/confirm/route'
import { t } from 'ember-intl'
import { or } from 'ember-truth-helpers'
import { UiButtonLink } from 'teacher-ui-client/components/ui-button/link'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import { on } from '@ember/modifier'
import { gradeName } from 'teacher-ui-client/helpers/grade-name'
import RouteTemplate from 'ember-route-template'
import PhosphorIcon from 'teacher-ui-client/components/phosphor-icon'
import {
  InlineAlert,
  AlertType,
  InlineAlertSubtitle,
  InlineAlertTitle,
} from 'teacher-ui-client/components/inline-alert'

interface Signature {
  Args: {
    model: ModelFor<ConfirmRoute>
    controller: ConfirmController
  }
}
export const RolloverStepsStudentsImportConfirmPage: TOC<Signature> = <template>
  <StudentImportPrompt
    @visible={{@controller.importPromptVisible}}
    @cancel={{@controller.hideImportPrompt}}
    @confirm={{@controller.submit}}
  />
  {{#if @controller.displayWarning}}
    <InlineAlert @type={{AlertType.Warning}} class="mx-auto mb-8 max-w-3xl">
      <InlineAlertTitle>
        <p data-test-filename>{{t
            "manage.rollover.steps.students.confirm.csvLoaded"
            filename=@controller.filename
          }}.</p>
      </InlineAlertTitle>
      <InlineAlertSubtitle>
        <div class="space-y-3">

          <ul class="ml-2 list-disc space-y-3">
            {{#if @controller.studentsNameChanged}}
              <li>{{t "manage.studentCsv.studentsNameChanged"}}</li>
            {{/if}}
            {{#if @controller.duplicateStudentsInSource}}
              <li>{{t "manage.studentCsv.duplicateStudentsInSource"}}</li>
            {{/if}}
            {{#if @controller.duplicateStudentsInDestination}}
              <li>{{t "manage.studentCsv.duplicateStudentsInDestination"}}</li>
            {{/if}}
          </ul>

          {{#if (or @controller.duplicateStudentsInSource @controller.duplicateStudentsInDestination)}}
            <p>{{t "manage.rollover.steps.students.duplicateStudents.solution"}}</p>
          {{/if}}

          <p>{{t "manage.rollover.steps.students.confirm.info.para1"}}</p>
          <p>{{t "manage.rollover.steps.students.confirm.info.para2"}}</p>
          <p>{{t "manage.rollover.steps.students.confirm.info.para3"}}</p>
          <p>{{t "manage.rollover.steps.students.confirm.info.para4"}}</p>
        </div>

      </InlineAlertSubtitle>
    </InlineAlert>
  {{else}}
    <InlineAlert @type={{AlertType.Information}} class="mx-auto max-w-3xl">
      <InlineAlertTitle>
        <p data-test-filename>{{t "manage.rollover.steps.students.confirm.csvLoaded" filename=@controller.filename}}</p>
      </InlineAlertTitle>
      <InlineAlertSubtitle>
        <div class="space-y-3">

          <p>{{t "manage.rollover.steps.students.confirm.completeCta"}}</p>
          <p>{{t "manage.rollover.steps.students.confirm.info.para1"}}</p>
          <p>{{t "manage.rollover.steps.students.confirm.info.para2"}}</p>
          <p>{{t "manage.rollover.steps.students.confirm.info.para3"}}</p>
          <p>{{t "manage.rollover.steps.students.confirm.info.para4"}}</p>
        </div>
      </InlineAlertSubtitle>
    </InlineAlert>
  {{/if}}

  <RolloverPreImportSummary class="mx-auto mt-12 max-w-3xl px-2" @candidateStudents={{@controller.candidateStudents}} />

  <div class="mx-auto mt-12 flex max-w-3xl justify-between px-2">
    <UiButtonLink
      data-test-cancel-import
      class="mr-8 w-40 whitespace-nowrap"
      @appearance={{ButtonAppearance.muted}}
      {{on "click" @controller.cancel}}
    >
      {{t "manage.bulkActions.csv.cancel"}}
    </UiButtonLink>
    <UiButton
      data-test-complete-import
      class="whitespace-nowrap"
      @appearance={{ButtonAppearance.regular}}
      {{on "click" @controller.showImportPrompt}}
    >
      {{t "manage.bulkActions.csv.submit"}}
    </UiButton>
  </div>

  <table class="mx-auto my-8 w-full max-w-6xl">
    <thead class="import-table-header">
      <tr>
        {{#if @controller.displayWarning}}
          <th class="import-table-cell import-table-header-cell w-16"></th>
        {{/if}}
        <th class="import-table-cell import-table-header-cell w-24 text-center font-semibold">{{t
            "manage.bulkActions.csv.lineNo"
          }}</th>
        <th class="import-table-cell import-table-header-cell font-semibold capitalize">{{t
            "manage.studentCsv.attribute.student-id"
          }}</th>
        <th class="import-table-cell import-table-header-cell font-semibold capitalize">{{t
            "manage.studentCsv.attribute.first-name"
          }}</th>
        <th class="import-table-cell import-table-header-cell font-semibold capitalize">{{t
            "manage.studentCsv.attribute.last-name"
          }}</th>
        <th class="import-table-cell import-table-header-cell font-semibold capitalize">{{t "grade.label"}}</th>
        <th class="import-table-cell import-table-header-cell font-semibold capitalize">{{t
            "manage.studentCsv.attribute.external-id"
          }}</th>
        {{#if @model.isSubco}}
          <th class="import-table-cell import-table-header-cell font-semibold capitalize">{{t
              "manage.studentCsv.attribute.teacher-email"
            }}</th>
        {{/if}}
        <th class="import-table-cell import-table-header-cell border-0 font-semibold">
          <span class="capitalize">{{t "manage.studentCsv.schoolClassName"}}</span>
        </th>
      </tr>
    </thead>
    <tbody class="import-table-body">
      {{#each @controller.candidateStudents as |student|}}
        <tr data-test-student-row>
          {{#if @controller.displayWarning}}
            <td class="import-table-cell import-table-body-cell text-center">
              {{#if (or student.duplicateSource student.duplicateDestination student.studentNameChange)}}
                <PhosphorIcon
                  @icon="phosphor-warning-fill"
                  class="text-juicy-orange-400 h-5 w-5"
                  data-test-warning-icon
                />
              {{/if}}
            </td>
          {{/if}}
          <td class="import-table-cell import-table-body-cell text-center">{{student.csvRow}}</td>
          <td class="import-table-cell import-table-body-cell">{{student.studentId}}</td>
          <td class="import-table-cell import-table-body-cell">{{student.firstName}}</td>
          <td class="import-table-cell import-table-body-cell">{{student.lastName}}</td>
          <td class="import-table-cell import-table-body-cell">{{gradeName student.gradePosition}}</td>
          <td class="import-table-cell import-table-body-cell">{{student.externalId}}</td>
          {{#if @model.isSubco}}
            <td class="import-table-cell import-table-body-cell">{{student.teacherEmail}}</td>
          {{/if}}
          <td class="import-table-cell import-table-body-cell">
            {{#if student.schoolClassName}}
              {{student.schoolClassName}}
            {{else}}
              {{t "manage.noClassAssigned"}}
            {{/if}}
          </td>
        </tr>
      {{/each}}
    </tbody>
  </table>
</template>

export default RouteTemplate(RolloverStepsStudentsImportConfirmPage)
